import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import AuthContext from './authContext';
import { publishCountsInvalidate } from '../util/countInvalidationBus';

const SocketIOContext = createContext({
  isConnected: false,
  on: () => () => {},
  off: () => {},
  emit: () => {},
});

export const SocketIOProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);
  const listenersRef = useRef(new Map());
  const authCtx = useContext(AuthContext);

  useEffect(() => {
    if (!authCtx.token) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setIsConnected(false);
      return;
    }

    const backendUrl = import.meta.env.VITE_BE_URL || 'http://localhost:3000';
    const socket = io(backendUrl, {
      transports: ['websocket', 'polling'],
      auth: {
        token: authCtx.token,
      },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    // Re-attach previously registered listeners (from prior socket instances)
    // to this new socket. socket.io-client preserves its own listener list
    // across reconnects of the same instance, so we do NOT re-attach on every
    // 'connect' event (that would cause duplicate handlers and duplicate toasts).
    for (const [event, handlers] of listenersRef.current.entries()) {
      for (const handler of handlers) socket.on(event, handler);
    }

    socket.on('connect', () => {
      setIsConnected(true);
    });

    socket.on('disconnect', reason => {
      setIsConnected(false);
      if (reason === 'io server disconnect') {
        socket.connect();
      }
    });

    socket.on('connect_error', err => {
      console.error('Socket.io connection error:', err.message);
      setIsConnected(false);
    });

    socket.on('token_expired', () => {
      setIsConnected(false);
      socket.disconnect();
      if (authCtx.logout) authCtx.logout();
    });

    socket.on('counts:invalidate', payload => {
      publishCountsInvalidate(payload);
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [authCtx.token]);

  const on = useCallback((event, handler) => {
    if (!event || typeof handler !== 'function') return () => {};

    const current = listenersRef.current.get(event) || new Set();
    current.add(handler);
    listenersRef.current.set(event, current);
    if (socketRef.current) socketRef.current.on(event, handler);

    return () => {
      const set = listenersRef.current.get(event);
      if (set) {
        set.delete(handler);
        if (set.size === 0) listenersRef.current.delete(event);
      }
      if (socketRef.current) socketRef.current.off(event, handler);
    };
  }, []);

  const off = useCallback((event, handler) => {
    const set = listenersRef.current.get(event);
    if (set) {
      set.delete(handler);
      if (set.size === 0) listenersRef.current.delete(event);
    }
    if (socketRef.current) socketRef.current.off(event, handler);
  }, []);

  const emit = useCallback((event, payload) => {
    if (socketRef.current) socketRef.current.emit(event, payload);
  }, []);

  return (
    <SocketIOContext.Provider value={{ isConnected, on, off, emit }}>
      {children}
    </SocketIOContext.Provider>
  );
};

export default SocketIOContext;
